import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input, Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { documentationApi } from '../../api/documentation.api'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'

const CATEGORY_SUGGESTIONS = [
  'Architektur',
  'Feature-Spezifikation',
  'API-Dokumentation',
  'Datenbankstruktur',
  'Entwicklungsrichtlinien',
  'Installation',
  'Testprotokolle',
  'Changelog',
  'Brainstorming',
]

export function NewDocumentModal({ projectId, onClose, onCreated }: { projectId: string; onClose: () => void; onCreated: (id: string) => void }) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => documentationApi.create({ projectId, title, category: category || undefined }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['documentation'], exact: false })
      toast.success('Dokument angelegt.')
      onCreated(res.data.id)
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <Modal
      title="Neues Dokument"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={mutation.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}
        <Field label="Titel" htmlFor="new-doc-title">
          <Input id="new-doc-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>
        <Field label="Kategorie (optional)" htmlFor="new-doc-category">
          <Select id="new-doc-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Keine Kategorie</option>
            {CATEGORY_SUGGESTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
      </form>
    </Modal>
  )
}
